import { SearchInput } from "@shared/components/SearchInput";
import { StepHelpers } from "@shared/components/Stepper";
import { toast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import {
  CheckCircle2,
  FileText,
  LetterText,
  LucideUserSquare,
  Mail,
  MapPin,
  Phone,
  User,
  Users,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useConsumerMetaData, useConsumerSearch } from "../../../hooks";
import { formatTerritoryAddress } from "@shared/utils/address";

interface CustomerSearchResult {
  id: string;
  label: string;
  description?: string;
  accountNumber: string;
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  customerType?: string;
  ownershipDisplay?: string;
  status: string;
  statusDisplay: string;
}

interface CustomerInformationStepProps {
  remoteUtilityId: number;
  storageKey: string;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

export const CustomerInformationStep: React.FC<
  CustomerInformationStepProps
> = ({
  remoteUtilityId,
  storageKey,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedConsumerId, setSelectedConsumerId] = useState<number | null>(
    null,
  );
  const [isMetadataProcessed, setIsMetadataProcessed] = useState(false);
  const [isAccountLoaded, setIsAccountLoaded] = useState(false);
  const [customerData, setCustomerData] = useState({
    accountNumber: "",
    customerName: "",
    serviceAddress: "",
    contactPhone: "",
    contactEmail: "",
    ownershipDisplay: "",
    status: "",
    statusDisplay: "",
  });
  const dataLoadedRef = useRef(false);

  // Use the consumer search hook
  const { data: searchResults, isLoading: isSearching } = useConsumerSearch({
    remoteUtilityId,
    requestType: "Reconnection",
    searchData: searchTerm,
  });

  // Use the consumer metadata hook to get detailed customer information
  const { data: consumerMetadata } = useConsumerMetaData({
    consumerId: selectedConsumerId || 0,
    remoteUtilityId,
  });

  // Helper function to save step data using stepHelpers
  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
        console.log("Step data saved via stepHelpers:", stepData);
      } catch (error) {
        console.error("Failed to save step data via stepHelpers:", error);
        toast({
          title: "Failed to save progress",
          description: "Your progress may not be saved. Please try again.",
          variant: "destructive",
        });
      }
    },
    [stepHelpers, currentStepIndex],
  );

  // Helper function to get step data using stepHelpers
  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data via stepHelpers:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Load saved data from stepHelpers on component mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          console.log("Loaded step data from stepHelpers:", savedStepData);

          // Populate customer data with saved data
          setCustomerData({
            accountNumber: savedStepData.accountNumber || "",
            customerName: savedStepData.customerName || "",
            serviceAddress: savedStepData.serviceAddress || "",
            contactPhone: savedStepData.contactPhone || "",
            contactEmail: savedStepData.contactEmail || "",
            ownershipDisplay: savedStepData.ownershipDisplay || "",
            status: savedStepData.status || "",
            statusDisplay: savedStepData.statusDisplay || "",
          });

          // Restore selected customer if available
          if (savedStepData.selectedConsumerId) {
            setSelectedConsumerId(savedStepData.selectedConsumerId);
            setIsAccountLoaded(true);
          }
        }
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step data from stepHelpers:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData]);

  // Update form with metadata when available
  useEffect(() => {
    if (
      consumerMetadata?.result &&
      selectedConsumerId &&
      !isMetadataProcessed
    ) {
      const metadata = consumerMetadata.result;

      // Build address string from service address components
      const addressParts = [
        metadata.serviceAddress.premise,
        metadata.serviceAddress.unit,
        metadata.serviceAddress.area,
        metadata.serviceAddress.subArea,
        metadata.serviceAddress.division,
        metadata.serviceAddress.zone,
        metadata.serviceAddress.county,
        metadata.serviceAddress.state,
        metadata.serviceAddress.zipcode,
      ].filter(Boolean);

      const fullAddress = addressParts.join(", ");

      const formattedTerritoryAddress = metadata.territoryData
        ? formatTerritoryAddress(metadata.territoryData, "service")
        : "";
      const finalAddress = formattedTerritoryAddress || fullAddress;

      // Update customer data with complete information
      const formData = {
        customerName: `${metadata.firstName} ${metadata.lastName}`,
        serviceAddress: finalAddress,
        territoryData: metadata.territoryData,
        consumerAddress: metadata.consumerAddress,
        contactPhone: metadata.contactNumber,
        contactEmail: metadata.email,
        accountNumber: metadata.consumerNo,
        ownershipDisplay: metadata.ownershipDisplay || "",
        status: metadata?.statusDisplay,
        statusDisplay: metadata?.statusDisplay,
      };

      setCustomerData(formData);

      // Save complete data using stepHelpers
      saveStepData({
        ...formData,
        selectedConsumerId: selectedConsumerId,
        consumerMetadata: metadata,
      });

      // Mark metadata as processed to prevent re-running
      setIsMetadataProcessed(true);
    }
  }, [consumerMetadata, saveStepData, isMetadataProcessed, selectedConsumerId]);

  // Reset metadata processed flag when a new customer is selected
  useEffect(() => {
    if (selectedConsumerId) {
      setIsMetadataProcessed(false);
    }
  }, [selectedConsumerId]);

  // Handle customer search
  const handleCustomerSearch = useCallback(
    async (term: string): Promise<CustomerSearchResult[]> => {
      if (term.length < 3) {
        return [];
      }

      setSearchTerm(term);

      // Map search results to the expected format
      const results =
        searchResults?.result?.map((consumer) => ({
          id: consumer.id.toString(),
          label: `${consumer.firstName} ${consumer.lastName}`,
          description: `${consumer.consumerNo}`,
          accountNumber: consumer.consumerNo,
          name: `${consumer.firstName} ${consumer.lastName}`,
          address: "", // Will be populated when customer is selected
          phone: "", // Will be populated when customer is selected
          email: "", // Will be populated when customer is selected
          customerType: consumer.statusDisplay,
          ownershipDisplay: consumer.ownershipDisplay || "",
          status: consumer.statusDisplay || "",
          statusDisplay: consumer.statusDisplay || "",
        })) || [];

      return results;
    },
    [searchResults],
  );

  // Handle customer selection
  const handleSelectCustomer = useCallback(
    (customer: CustomerSearchResult) => {
      const consumerId = parseInt(customer.id);
      setSelectedConsumerId(consumerId);

      // Update customer data with basic information
      setCustomerData((prev) => ({
        ...prev,
        customerName: customer.name,
        accountNumber: customer.accountNumber,
        ownershipDisplay: customer.ownershipDisplay || "",
        status: customer.statusDisplay || "",
      }));

      // Save initial customer selection data using stepHelpers
      saveStepData({
        accountNumber: customer.accountNumber,
        customerName: customer.name,
        selectedConsumerId: consumerId,
        selectedCustomer: customer,
        isCustomerSelected: true,
      });

      setIsAccountLoaded(true);
    },
    [saveStepData],
  );

  // Render customer search result
  const renderCustomerResult = useCallback(
    (result: CustomerSearchResult) => (
      <div className="p-3 hover:bg-blue-50 cursor-pointer rounded border-l-4 border-transparent hover:border-blue-400 transition-all">
        <div className="font-medium text-gray-900">{result.name}</div>
        <div className="text-sm text-gray-600">
          Account {result.accountNumber}
        </div>
        {result.customerType && (
          <div className="text-xs text-gray-500">{result.customerType}</div>
        )}
      </div>
    ),
    [],
  );

  const handleContinue = () => {
    // Clear any previous validation errors
    if (clearValidationError) {
      clearValidationError();
    }

    // Save final data using stepHelpers
    saveStepData({
      ...customerData,
      selectedConsumerId,
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    });

    // Proceed to next step
    if (onNext) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-600">
          Search and select a customer to begin the service request.
        </p>
      </div>

      {/* Customer Search Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Users className="h-5 w-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">
            Customer Search
          </h3>
        </div>

        <div className="relative">
          <SearchInput
            placeholder="Search by account number, name, phone or address..."
            onSearch={handleCustomerSearch}
            onSelect={handleSelectCustomer}
            renderResult={renderCustomerResult}
            minSearchLength={3}
            className="w-full"
            debounceMs={200}
          />
          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
            <FileText className="h-3 w-3" />
            Type at least 3 characters to search existing customers
          </p>
        </div>
      </div>

      {/* Selected Customer Information Display */}
      {selectedConsumerId && (
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 mb-3">
            <User className="h-4 w-4 text-green-600" />
            <h4 className="font-medium text-green-800">Selected Customer</h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <LucideUserSquare className="h-4 w-4 text-gray-500" />
              <span className="font-medium text-gray-700">Account:</span>
              <span className="text-gray-900">
                {customerData.accountNumber || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <LetterText className="h-4 w-4 text-gray-500" />
              <span className="font-medium text-gray-700">Name:</span>
              <span className="text-gray-900">
                {customerData.customerName || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-500" />
              <span className="font-medium text-gray-700">Phone:</span>
              <span className="text-gray-900">
                {customerData.contactPhone || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-500" />
              <span className="font-medium text-gray-700">Email:</span>
              <span className="text-gray-900">
                {customerData.contactEmail || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-gray-500" />
              <span className="font-medium text-gray-700">Ownership:</span>
              <span className="text-gray-900">
                {customerData.ownershipDisplay || "N/A"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span className="font-medium text-gray-700">Status:</span>
              <span className="inline-flex items-center gap-1">
                <span className="text-gray-900">
                  {customerData.statusDisplay || "N/A"}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <MapPin className="h-4 w-4 text-gray-500" />
              <span className="font-medium text-gray-700">Address:</span>
              <span className="text-gray-900">
                {customerData.serviceAddress || "N/A"}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-4">
        <Button
          onClick={handleContinue}
          disabled={!isAccountLoaded || isValidating}
          className="min-w-[200px]"
        >
          {isValidating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Validating...
            </>
          ) : (
            "Continue to reconnection Selection"
          )}
        </Button>
      </div>
    </div>
  );
};
